// XFollowPanel — Producer-only "Follow & Earn" panel for the Dashboard.
//
// The synergy loop: a Producer connects @GlobalEyesDOTtv on X, picks
// ≥1 of our public lists to follow, and earns +10 credits every month
// they keep following them. We lazily re-verify once per quarter on
// `GET /api/x/status` (no cron, no background workers — saves API
// spend). The same endpoint drops the monthly bonus the first time
// they hit Dashboard each calendar month.
//
// Surfaces only for `subscription_tier === 'producer'`. Hides entirely
// when X integration isn't configured server-side (returns 503 →
// `configured: false`).
//
// Props:
//   user            — full user object from AuthContext.
//   refreshUser     — pulls a fresh /auth/me snapshot so the credit
//                     balance in the header updates after a bonus drop.
//   autoOpenPicker  — boolean; when true (post-subscription redirect)
//                     the panel scrolls into view and expands the
//                     list-picker once a connection exists.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, Loader2, RefreshCw, Sparkles, X as XIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import api from "../../lib/apiClient";

const POPUP_FEATURES = "width=600,height=720,scrollbars=yes,resizable=yes";

const XFollowPanel = ({ user, refreshUser, autoOpenPicker = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerLists, setOwnerLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [picker, setPicker] = useState(false);
  const [chosen, setChosen] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  const isProducer = (user?.subscription_tier || "free").toLowerCase() === "producer";

  const refreshStatus = useCallback(async () => {
    try {
      const res = await api.get("/x/status");
      setStatus(res.data);
      if (res.data?.chosen_list_ids?.length) {
        setChosen(new Set(res.data.chosen_list_ids));
      }
      if (res.data?.credits_granted_now > 0) {
        toast.success(`+${res.data.credits_granted_now} credits — this month's X loyalty bonus 🎉`);
        refreshUser?.();
      }
    } catch (err) {
      // 503 means X integration not configured — degrade silently.
      if (err.response?.status !== 503) {
        console.error("x/status failed", err);
      }
      setStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isProducer) refreshStatus();
    else setLoading(false);
  }, [isProducer, refreshStatus]);

  // Post-subscription deep-link — scroll into view and pop the picker.
  useEffect(() => {
    if (!autoOpenPicker || !status?.connected || loading) return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setPicker(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenPicker, status?.connected, loading]);

  const loadOwnerLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const res = await api.get("/x/owner-lists");
      setOwnerLists(res.data?.lists || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load lists from X.");
    } finally {
      setLoadingLists(false);
    }
  }, []);

  const openPicker = async () => {
    if (!ownerLists.length) await loadOwnerLists();
    setPicker(true);
  };

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.post("/x/connect/start", {});
      const { auth_url, state } = res.data;
      // Open in a popup so we can receive the {code, state} via postMessage
      // from /auth/x/callback without losing dashboard state.
      const popup = window.open(auth_url, "x_oauth", POPUP_FEATURES);
      if (!popup) {
        toast.error("Allow popups for globaleyes.tv, then try again.");
        setBusy(false);
        return;
      }
      const onMsg = async (ev) => {
        // Same-origin postMessage only.
        if (ev.origin !== window.location.origin) return;
        if (ev.data?.type !== "x_oauth_callback") return;
        if (ev.data.state !== state) {
          toast.error("X sign-in state mismatch.");
          setBusy(false);
          window.removeEventListener("message", onMsg);
          return;
        }
        if (ev.data.error) {
          toast.error(`X connect failed: ${ev.data.error}`);
          setBusy(false);
          window.removeEventListener("message", onMsg);
          return;
        }
        try {
          await api.post("/x/connect/callback", { code: ev.data.code, state: ev.data.state });
          toast.success("X account connected. Now pick a list to follow.");
          await refreshStatus();
          await loadOwnerLists();
          setPicker(true);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't finish X connect.");
        } finally {
          setBusy(false);
          window.removeEventListener("message", onMsg);
        }
      };
      window.addEventListener("message", onMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start X sign-in.");
      setBusy(false);
    }
  };

  const submitPicks = async () => {
    const ids = Array.from(chosen);
    const minReq = status?.min_lists ?? 1;
    if (ids.length < minReq) {
      toast.error(`Pick at least ${minReq} list${minReq === 1 ? "" : "s"}.`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/x/verify", { list_ids: ids });
      toast.success(
        res.data?.credits_granted_now > 0
          ? `Verified! +${res.data.credits_granted_now} credits added.`
          : "Verified! You're eligible — credits drop on the 1st of each month.",
      );
      setPicker(false);
      await refreshStatus();
      refreshUser?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.missing_list_ids) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Verification failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect X? You'll lose the monthly +10 credit bonus.")) return;
    setBusy(true);
    try {
      await api.post("/x/disconnect");
      toast.info("X disconnected.");
      setStatus({ configured: true, connected: false, owner_handle: status?.owner_handle, reward_per_month: status?.reward_per_month, min_lists: status?.min_lists });
      setChosen(new Set());
      setPicker(false);
    } catch (err) {
      toast.error("Couldn't disconnect.");
    } finally {
      setBusy(false);
    }
  };

  // Hide entirely for non-producers or when X integration not configured.
  if (loading) return null;
  if (!isProducer) return null;
  if (!status?.configured) return null;

  const ownerHandle = status.owner_handle || "GlobalEyesDOTtv";
  const reward = status.reward_per_month || 10;
  const minLists = status.min_lists ?? 1;
  const listWord = minLists === 1 ? "list" : "lists";

  return (
    <Card
      ref={rootRef}
      data-testid="x-follow-panel"
      className="bg-gradient-to-br from-sky-950/40 via-gray-900 to-gray-900 border-sky-700/40 mb-6"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="bg-black border border-sky-700/40 rounded-lg p-2 shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-base flex items-center gap-2">
              Follow {`@${ownerHandle}`} on X — earn +{reward} credits/month
              {status.eligible && (
                <span className="text-xs bg-emerald-700/40 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-600/40">Active</span>
              )}
            </h3>
            <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
              Producer perk: follow at least {minLists} of our public X {listWord}.
              We re-check once a quarter — keep following, keep earning.
            </p>
          </div>
        </div>

        {!status.connected && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={connect}
              disabled={busy}
              data-testid="x-connect-btn"
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Connect my X account
            </Button>
            <a
              href={`https://x.com/${ownerHandle}/lists`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center text-sky-400 text-sm hover:text-sky-300 px-3 py-2"
            >
              Preview the lists <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </a>
          </div>
        )}

        {status.connected && !picker && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              {status.x_profile_image && (
                <img src={status.x_profile_image} alt="" className="w-6 h-6 rounded-full" />
              )}
              <span className="text-white">@{status.x_handle}</span>
              {status.eligible ? (
                <span className="text-emerald-400 inline-flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" />
                  Following {status.followed_list_ids?.length || 0} {listWord}
                </span>
              ) : (
                <span className="text-amber-400">Pick your {listWord} →</span>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button
                size="sm"
                variant="outline"
                onClick={openPicker}
                disabled={busy}
                data-testid="x-pick-lists-btn"
                className="border-sky-700/50 text-sky-300 hover:bg-sky-900/30"
              >
                {status.eligible ? <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> : null}
                {status.eligible ? "Change lists" : `Pick ${minLists}+ ${listWord}`}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={disconnect}
                disabled={busy}
                data-testid="x-disconnect-btn"
                className="text-gray-500 hover:text-rose-400"
              >
                <XIcon className="w-3.5 h-3.5 mr-1" />
                Disconnect
              </Button>
            </div>
          </div>
        )}

        {status.connected && picker && (
          <div className="mt-3 border-t border-gray-800 pt-3">
            <p className="text-gray-300 text-xs sm:text-sm mb-2">
              Tick at least <span className="text-white font-semibold">{minLists}</span> {listWord} you'll follow on X.
              You must already be (or become) a follower for verification to pass — we'll tell you which are missing if not.
            </p>
            {loadingLists ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading lists from X…
              </div>
            ) : ownerLists.length === 0 ? (
              <p className="text-gray-500 text-sm py-3">
                No public lists found for @{ownerHandle}. Try again later.
              </p>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1 pr-1" data-testid="x-list-picker">
                {ownerLists.map((lst) => {
                  const on = chosen.has(lst.id);
                  return (
                    <label
                      key={lst.id}
                      className={`flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                        on
                          ? "bg-sky-900/30 border-sky-700/60"
                          : "bg-gray-800/40 border-transparent hover:bg-gray-800/70"
                      }`}
                      data-testid={`x-list-row-${lst.id}`}
                    >
                      <Checkbox
                        checked={on}
                        onCheckedChange={(checked) => {
                          const next = new Set(chosen);
                          if (checked) next.add(lst.id);
                          else next.delete(lst.id);
                          setChosen(next);
                        }}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium truncate">{lst.name}</span>
                          <a
                            href={lst.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-sky-400 hover:text-sky-300 text-xs inline-flex items-center"
                          >
                            <ExternalLink className="w-3 h-3 ml-0.5" />
                          </a>
                        </div>
                        {lst.description && (
                          <p className="text-gray-500 text-xs line-clamp-1">{lst.description}</p>
                        )}
                        <p className="text-gray-600 text-[11px] mt-0.5">
                          {lst.member_count} members · {lst.follower_count} followers
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between mt-3">
              <span className="text-gray-400 text-xs">
                Selected: <span className={`font-semibold ${chosen.size >= minLists ? "text-emerald-400" : "text-amber-400"}`}>{chosen.size}</span> / {minLists}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPicker(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={submitPicks}
                  disabled={busy || chosen.size < minLists}
                  data-testid="x-verify-btn"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                  Verify &amp; activate
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default XFollowPanel;
